import React, { useState, useEffect } from 'react';
import { httpsCallable, Functions } from 'firebase/functions';

interface ApiKeyGeneratorProps {
  functions: Functions;
  userEmail?: string | null;
}

const ApiKeyGenerator: React.FC<ApiKeyGeneratorProps> = ({ functions, userEmail }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setGeneratedToken(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timeRemaining]);

  const handleGenerateToken = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedToken(null);

    try {
      const updateRefreshToken = httpsCallable(functions, 'updateRefreshToken');
      const result = await updateRefreshToken();
      
      if (result.data && typeof result.data === 'object' && 'token' in result.data) {
        const tokenData = result.data as { token: string; success: boolean; message: string };
        setGeneratedToken(tokenData.token);
        setTimeRemaining(60); // 60 seconds countdown
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err: any) {
      console.error('Error generating API key:', err);
      setError(err.message || 'Failed to generate API key');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (generatedToken) {
      try {
        await navigator.clipboard.writeText(generatedToken);
        // You could add a toast notification here
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="api-key-generator" style={{ 
      padding: '24px', 
      border: '1px solid #e0e0e0', 
      borderRadius: '8px',
      backgroundColor: '#fafafa',
      maxWidth: '600px'
    }}>
      <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>
        API Key Management
      </h3>
      
      {!generatedToken ? (
        <div>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Generate a new API key for your account. This will invalidate any existing API key.
          </p>
          
          <button
            onClick={handleGenerateToken}
            disabled={isGenerating}
            style={{
              padding: '12px 24px',
              backgroundColor: isGenerating ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            {isGenerating ? 'Generating...' : `Generate API Key for ${userEmail}`}
          </button>
          
          {error && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c33'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{
            padding: '16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#856404' }}>
              ⚠️ Important: Store this API key safely
            </h4>
            <p style={{ margin: '0', color: '#856404' }}>
              This API key will only be displayed for <strong>{formatTime(timeRemaining)}</strong>. 
              After that, you won't be able to view it again. Store it in a secure location.
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#155724' }}>
              Your API Key:
            </h4>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <code style={{
                flex: 1,
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                wordBreak: 'break-all',
                overflow: 'auto'
              }}>
                {generatedToken}
              </code>
              <button
                onClick={copyToClipboard}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
                title="Copy to clipboard"
              >
                Copy
              </button>
            </div>
          </div>

          <div style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#6c757d'
          }}>
            <p style={{ margin: '0 0 8px 0' }}>
              <strong>Usage:</strong> Include this key in your API requests as a Bearer token:
            </p>
            <code style={{ 
              display: 'block',
              padding: '8px',
              backgroundColor: '#e9ecef',
              borderRadius: '2px',
              fontFamily: 'monospace'
            }}>
              Authorization: Bearer {generatedToken.substring(0, 20)}...
            </code>
          </div>

          <button
            onClick={() => {
              setGeneratedToken(null);
              setTimeRemaining(0);
            }}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Generate New Key
          </button>
        </div>
      )}
    </div>
  );
};

export default ApiKeyGenerator;